<template>
  <v-layout>
    <v-main :class="containerState">
      <v-container id="main-container" class="pb-0" :fluid="true">
        <AppBar />

        <v-card class="d-flex flex-column fill-height" flat>
          <v-card-title class="text-h6 px-6 pt-6">
            {{ $t("labels.summary-title") }}
          </v-card-title>

          <v-card-subtitle class="pl-6" v-if="modelDT?.description">
            {{ modelDT.description }}
          </v-card-subtitle>

          <v-card-text
            class="px-6 d-flex flex-column justify-center align-center"
          >
            <div class="d-flex flex-column align-center" style="width: 100%">
              <v-timeline direction="horizontal">
                <v-timeline-item
                  v-for="step in steps"
                  :key="step.title"
                  :dot-color="step.dotColor"
                  :icon="step.icon"
                  fill-dot
                  side="start"
                >
                  <div class="d-flex flex-column align-center text-center">
                    <div class="text-h6">{{ $t(step.title) }}</div>
                    <p class="mt-2 mb-2">{{ $t(step.description) }}</p>
                    <v-btn
                      prepend-icon="mdi-pencil"
                      color="secondary"
                      variant="outlined"
                      size="small"
                      :disabled="step.disabled"
                      @click="routeTo(step.route)"
                    >
                      {{ $t(step.label) }}
                    </v-btn>
                  </div>
                </v-timeline-item>
              </v-timeline>
              <v-card
                class="mt-6"
                variant="outlined"
                :loading="loaderPublishModel"
              >
                <v-card-title class="text-h6">
                  {{ $t("strings.model-status") }}
                </v-card-title>
                <v-card-subtitle>
                  <v-chip
                    v-if="disabledPublishModel"
                    color="red"
                    variant="flat"
                  >
                    {{ $t("strings.incomplete-configuration-status") }}
                  </v-chip>
                  <v-chip v-else-if="publishModel" color="green" variant="flat">
                    {{ $t("strings.publish-status") }}
                  </v-chip>
                  <v-chip v-else-if="!publishModel" color="grey" variant="flat">
                    {{ $t("strings.not-publish-status") }}
                  </v-chip>
                </v-card-subtitle>
                <v-card-text>
                  {{
                    $t(
                      disabledPublishModel
                        ? "strings.incomplete-configuration-description"
                        : publishModel
                          ? "strings.publish-description"
                          : "strings.not-publish-description",
                    )
                  }}
                </v-card-text>
                <v-card-actions v-if="!disabledPublishModel">
                  <v-spacer />
                  <v-btn
                    color="primary"
                    variant="outlined"
                    :disabled="!hasPermission"
                    @click="publishModel ? undeploy() : deploy()"
                  >
                    {{
                      $t(
                        publishModel
                          ? "labels.remove-publish"
                          : "labels.publish",
                      )
                    }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-btn color="secondary" variant="outlined" @click="abortModel">
              {{ $t("labels.cancel") }}
            </v-btn>
            <v-spacer />
            <v-btn
              variant="outlined"
              class="bg-secondary"
              color="white"
              @click="routeTo('/editor/conversion')"
            >
              {{ $t("labels.back") }}
            </v-btn>
            <v-btn
              variant="outlined"
              class="bg-secondary"
              color="white"
              :disabled="!hasPermission"
              @click="updateModel"
            >
              {{ modelDT?.newModel ? $t("labels.save") : $t("labels.update") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-container>
    </v-main>
    <!--Snackbar-->
    <v-snackbar v-model="snackbar" top color="green" timeout="5000">
      {{ snackbarText }}
      <template v-slot:actions="{ attrs }">
        <v-btn
          color="white"
          variant="text"
          v-bind="attrs"
          @click="snackbar = false"
        >
          OK
        </v-btn>
      </template>
    </v-snackbar>
  </v-layout>
</template>

<script>
import AppBar from "./components/AppBar.vue";
import version from "../version";
import events from "@/mixins/events.js";
import utils from "@/mixins/utils.js";
import AppMixin from "@/mixins/AppMixin.js";
import permissions from "@/mixins/permissions.js";

export default {
  name: "DigitalTwinsSummary",
  components: {
    AppBar,
  },
  mixins: [utils, events, AppMixin, permissions],
  props: ["digitalTwinModelSummaryConfig", "digitalTwinCommonConfig", "realm"],
  data() {
    return {
      appVersion: version,
      containerState: "expanded",
      modelDT: JSON.parse(sessionStorage.getItem("digital-twin-model")),
      snackbar: false,
      snackbarText: "",
      steps: [],
      publishModel: false,
      disabledPublishModel: false,
      loaderPublishModel: false,
    };
  },
  created() {
    if (!this?.modelDT) {
      this.abortModel();
      return;
    }

    this.initSteps();
  },
  mounted() {
    document.addEventListener(
      "language-localstorage-changed",
      this.languageLocalstorageChanged,
    );

    this.containerState =
      localStorage.getItem("sidenav") === "opened" ? "reduced" : "expanded";

    document.addEventListener("sidenav-localstorage-changed", (event) => {
      this.containerState =
        event.detail.storage === "opened" ? "reduced" : "expanded";
    });
  },
  beforeUnmount() {
    document.removeEventListener(
      "language-localstorage-changed",
      this.languageLocalstorageChanged,
    );
  },
  methods: {
    initSteps() {
      const model = this.modelDT;

      this.publishModel = !!model.active;

      const hasOutdatedEntities = model.hasOutdatedEntities;

      const stepEditorDefinitionCompleted = model.modelGraph?.nodes?.length > 0;

      const stepDataDefinitionCompleted = model.modelGraph?.nodes?.every(
        (node) =>
          node?.data?.category === "Structure" || !!node?.data?.bindings,
      );

      const stepConversionCompleted = model.standards?.length > 0;

      this.disabledPublishModel =
        !stepEditorDefinitionCompleted ||
        !stepDataDefinitionCompleted ||
        !stepConversionCompleted ||
        hasOutdatedEntities;

      const createStep = ({
        title,
        descriptionSuccess,
        descriptionError,
        route,
        completed,
        validate = false,
        disabled = false,
      }) => ({
        title,
        description: validate
          ? "labels.step-editor-definition-description-obsolete"
          : completed
            ? descriptionSuccess
            : descriptionError,
        completed,
        validate,
        disabled,
        label: this.hasPermission ? "labels.edit" : "labels.view",
        route,
        dotColor: disabled
          ? "grey-lighten-1"
          : validate
            ? "red"
            : completed
              ? "green"
              : "grey",
        icon: disabled
          ? "mdi-minus"
          : validate
            ? "mdi-exclamation"
            : completed
              ? "mdi-check"
              : "mdi-exclamation",
      });

      this.steps = [
        createStep({
          title: "labels.step-editor-definition-title",
          descriptionSuccess:
            "labels.step-editor-definition-description-success",
          descriptionError: "labels.step-editor-definition-description-error",
          completed: stepEditorDefinitionCompleted,
          validate: hasOutdatedEntities,
          disabled: false,
          route: "/editor/definition",
        }),

        createStep({
          title: "labels.step-data-definition-title",
          descriptionSuccess: "labels.step-data-definition-description-success",
          descriptionError: "labels.step-data-definition-description-error",
          completed: stepDataDefinitionCompleted,
          disabled: hasOutdatedEntities,
          route: "/editor/data",
        }),

        createStep({
          title: "labels.step-conversion-title",
          descriptionSuccess: "labels.step-conversion-description-success",
          descriptionError: "labels.step-conversion-description-error",
          completed: stepConversionCompleted,
          disabled: hasOutdatedEntities,
          route: "/editor/conversion",
        }),
      ];
    },
    languageLocalstorageChanged(event) {
      this.setLocale(event.detail.storage);
    },
  },
};
</script>

<style scoped>
main.expanded {
  margin-left: 56px;
}
main.expanded header#upp-bar {
  margin-left: 56px;
  width: calc(100% - 56px) !important;
}
main.reduced {
  margin-left: 256px;
}
main.reduced header#upp-bar {
  margin-left: 256px;
  width: calc(100% - 256px) !important;
}
#main-container {
  height: calc(100vh - 104px);
  overflow-y: auto;
  background-color: white;
}
</style>
